import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Plus, Play, Save, Calendar, GitBranch, AlertTriangle, CheckCircle2,
  Clock, BarChart3, Download, Upload, Milestone, Network, Settings, RefreshCw,
  ChevronRight, ChevronDown, Trash2, Link, Zap
} from 'lucide-react';
import '@/styles/frappe-gantt.css';

const CPMScheduler = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ganttContainerRef = useRef(null);
  const ganttRef = useRef(null);

  // State
  const [project, setProject] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [activities, setActivities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [qualityReport, setQualityReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [activeTab, setActiveTab] = useState('gantt');
  const [viewMode, setViewMode] = useState('Week');

  // Dialog states
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [showCreateBaseline, setShowCreateBaseline] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Form states
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    planned_start: new Date().toISOString().split('T')[0],
    must_finish_by: '',
    description: ''
  });
  const [activityForm, setActivityForm] = useState({
    activity_id: '',
    name: '',
    original_duration: 5,
    activity_type: 'task',
    constraint_type: 'asap',
    constraint_date: '',
    description: ''
  });
  const [relationshipForm, setRelationshipForm] = useState({
    predecessor_id: '',
    successor_id: '',
    relationship_type: 'FS',
    lag: 0
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch project
      const projectRes = await api.projects.getById(projectId);
      setProject(projectRes.data.project || projectRes.data);

      // Try to fetch schedule
      try {
        const scheduleRes = await api.cpm.getSchedule(projectId);
        setSchedule(scheduleRes.data);

        // Fetch related data
        const [activitiesRes, relationshipsRes, calendarsRes, baselinesRes] = await Promise.all([
          api.cpm.getActivities(projectId),
          api.cpm.getRelationships(projectId),
          api.cpm.getCalendars(projectId),
          api.cpm.getBaselines(projectId)
        ]);

        setActivities(activitiesRes.data || []);
        setRelationships(relationshipsRes.data || []);
        setCalendars(calendarsRes.data || []);
        setBaselines(baselinesRes.data || []);
      } catch (err) {
        if (err.response?.status === 404) {
          setSchedule(null);
        } else {
          throw err;
        }
      }
    } catch (error) {
      console.error('Error fetching CPM data:', error);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (user && projectId) {
      fetchData();
    }
  }, [fetchData, user, projectId]);

  // Initialize Gantt chart
  useEffect(() => {
    const initGantt = async () => {
      if (!ganttContainerRef.current || activities.length === 0) return;

      try {
        const Gantt = (await import('frappe-gantt')).default;

        // Convert activities to Gantt tasks
        const tasks = activities.map(act => ({
          id: act.id,
          name: act.name,
          start: act.early_start || act.planned_start || schedule?.planned_start || new Date().toISOString().split('T')[0],
          end: act.early_finish || act.planned_finish || schedule?.planned_start || new Date().toISOString().split('T')[0],
          progress: act.percent_complete || 0,
          dependencies: relationships
            .filter(r => r.successor_id === act.id)
            .map(r => r.predecessor_id)
            .join(', '),
          custom_class: act.is_critical ? 'critical-task' : ''
        }));

        // Clear existing chart
        if (ganttRef.current) {
          ganttContainerRef.current.innerHTML = '';
        }

        ganttRef.current = new Gantt(ganttContainerRef.current, tasks, {
          view_mode: viewMode,
          date_format: 'YYYY-MM-DD',
          popup_trigger: 'click',
          on_click: (task) => {
            const act = activities.find(a => a.id === task.id);
            setSelectedActivity(act);
          },
          on_date_change: async (task, start, end) => {
            try {
              await api.cpm.updateActivity(projectId, task.id, {
                planned_start: start.toISOString().split('T')[0],
                planned_finish: end.toISOString().split('T')[0]
              });
              toast.success('Activity dates updated');
              fetchData();
            } catch (error) {
              toast.error('Failed to update activity');
            }
          },
          on_progress_change: async (task, progress) => {
            try {
              await api.cpm.updateActivity(projectId, task.id, {
                percent_complete: progress
              });
              toast.success('Progress updated');
            } catch (error) {
              toast.error('Failed to update progress');
            }
          },
          custom_popup_html: (task) => {
            const act = activities.find(a => a.id === task.id);
            return `
              <div class="gantt-popup">
                <h4 class="font-semibold">${task.name}</h4>
                <p class="text-sm text-slate-600">${act?.activity_id || ''}</p>
                <div class="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div><span class="text-slate-500">Start:</span> ${task.start}</div>
                  <div><span class="text-slate-500">Finish:</span> ${task.end}</div>
                  <div><span class="text-slate-500">Duration:</span> ${act?.original_duration || 0}d</div>
                  <div><span class="text-slate-500">Float:</span> ${act?.total_float ?? '-'}d</div>
                </div>
                ${act?.is_critical ? '<div class="mt-2"><span class="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Critical Path</span></div>' : ''}
              </div>
            `;
          }
        });
      } catch (error) {
        console.error('Failed to initialize Gantt chart:', error);
      }
    };

    if (activeTab === 'gantt') {
      initGantt();
    }
  }, [activities, relationships, schedule, viewMode, activeTab, projectId, fetchData]);

  // Handle view mode change
  useEffect(() => {
    if (ganttRef.current) {
      ganttRef.current.change_view_mode(viewMode);
    }
  }, [viewMode]);

  // Create schedule
  const handleCreateSchedule = async () => {
    try {
      const data = {
        ...scheduleForm,
        data_date: scheduleForm.planned_start
      };
      await api.cpm.createSchedule(projectId, data);
      toast.success('Schedule created successfully');
      setShowCreateSchedule(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  // Add activity
  const handleAddActivity = async () => {
    try {
      await api.cpm.createActivity(projectId, activityForm);
      toast.success('Activity added successfully');
      setShowAddActivity(false);
      setActivityForm({
        activity_id: '',
        name: '',
        original_duration: 5,
        activity_type: 'task',
        constraint_type: 'asap',
        constraint_date: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add activity');
    }
  };

  // Add relationship
  const handleAddRelationship = async () => {
    try {
      await api.cpm.createRelationship(projectId, relationshipForm);
      toast.success('Relationship added successfully');
      setShowAddRelationship(false);
      setRelationshipForm({
        predecessor_id: '',
        successor_id: '',
        relationship_type: 'FS',
        lag: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add relationship');
    }
  };

  // Compute schedule
  const handleCompute = async () => {
    try {
      setComputing(true);
      const result = await api.cpm.compute(projectId);
      toast.success(`Schedule computed in ${result.data.compute_time_ms}ms`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Computation failed');
    } finally {
      setComputing(false);
    }
  };

  // Quality check
  const handleQualityCheck = async () => {
    try {
      const result = await api.cpm.qualityCheck(projectId);
      setQualityReport(result.data);
      toast.success('Quality check complete');
    } catch (error) {
      toast.error('Quality check failed');
    }
  };

  // Create baseline
  const handleCreateBaseline = async (name, description) => {
    try {
      await api.cpm.createBaseline(projectId, { name, description, is_primary: baselines.length === 0 });
      toast.success('Baseline created');
      setShowCreateBaseline(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create baseline');
    }
  };

  // Delete activity
  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Delete this activity and its relationships?')) return;
    try {
      await api.cpm.deleteActivity(projectId, activityId);
      toast.success('Activity deleted');
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg text-slate-600">Loading schedule...</div>
        </div>
      </DashboardLayout>
    );
  }

  // No schedule exists - show creation prompt
  if (!schedule) {
    return (
      <DashboardLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Project
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                CPM Scheduling
              </CardTitle>
              <CardDescription>
                No schedule exists for this project. Create one to start planning activities.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Schedule Name</Label>
                  <Input
                    value={scheduleForm.name}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                    placeholder={`${project?.name || 'Project'} Master Schedule`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Planned Start</Label>
                    <Input
                      type="date"
                      value={scheduleForm.planned_start}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, planned_start: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Contract Completion</Label>
                    <Input
                      type="date"
                      value={scheduleForm.must_finish_by}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, must_finish_by: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={scheduleForm.description}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                    placeholder="Schedule description..."
                  />
                </div>
                <Button onClick={handleCreateSchedule} className="w-full" data-testid="create-schedule-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Main scheduler view
  return (
    <DashboardLayout>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-xl font-bold">{schedule.name || 'CPM Schedule'}</h1>
                <p className="text-sm text-slate-500">{project?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Day">Day</SelectItem>
                  <SelectItem value="Week">Week</SelectItem>
                  <SelectItem value="Month">Month</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleQualityCheck} data-testid="quality-check-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Quality Check
              </Button>
              <Button onClick={handleCompute} disabled={computing} data-testid="compute-btn">
                {computing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                Compute
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2 text-sm">
              <BarChart3 className="h-4 w-4 text-slate-500" />
              <span className="font-medium">{activities.length}</span> Activities
            </div>
            <div className="flex items-center gap-2 text-sm">
              <GitBranch className="h-4 w-4 text-slate-500" />
              <span className="font-medium">{relationships.length}</span> Links
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-red-500" />
              <span className="font-medium">{activities.filter(a => a.is_critical).length}</span> Critical
            </div>
            {schedule.schedule_health_score !== null && (
              <Badge variant={schedule.schedule_health_score >= 80 ? 'success' : schedule.schedule_health_score >= 50 ? 'warning' : 'destructive'}>
                Health: {schedule.schedule_health_score}%
              </Badge>
            )}
            {schedule.early_finish && (
              <div className="text-sm text-slate-600">
                <span className="font-medium">Finish:</span> {schedule.early_finish}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-4 border-b bg-slate-50">
            <TabsList className="h-12">
              <TabsTrigger value="gantt" className="gap-2">
                <BarChart3 className="h-4 w-4" /> Gantt Chart
              </TabsTrigger>
              <TabsTrigger value="activities" className="gap-2">
                <Clock className="h-4 w-4" /> Activities
              </TabsTrigger>
              <TabsTrigger value="relationships" className="gap-2">
                <Link className="h-4 w-4" /> Relationships
              </TabsTrigger>
              <TabsTrigger value="baselines" className="gap-2">
                <Milestone className="h-4 w-4" /> Baselines
              </TabsTrigger>
              <TabsTrigger value="quality" className="gap-2">
                <AlertTriangle className="h-4 w-4" /> Quality
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Gantt Tab */}
          <TabsContent value="gantt" className="flex-1 m-0 overflow-hidden">
            {activities.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8">
                <Calendar className="h-16 w-16 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium mb-2">No activities yet</h3>
                <p className="text-sm text-slate-500 mb-4">Add activities to start building your schedule</p>
                <Button onClick={() => setShowAddActivity(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Activity
                </Button>
              </div>
            ) : (
              <div className="h-full overflow-auto p-4">
                <style>{`
                  .gantt-container { font-family: inherit; }
                  .gantt .bar-wrapper.critical-task .bar { fill: #ef4444 !important; }
                  .gantt .bar-wrapper.critical-task .bar-progress { fill: #dc2626 !important; }
                  .gantt .bar-label { fill: #fff; font-weight: 500; }
                  .gantt-popup { padding: 12px; min-width: 200px; }
                `}</style>
                <div ref={ganttContainerRef} className="gantt-container" data-testid="gantt-chart" />
              </div>
            )}
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities" className="flex-1 m-0 overflow-auto">
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Activities</h3>
                <Button onClick={() => setShowAddActivity(true)} data-testid="add-activity-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Activity
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">ID</th>
                      <th className="px-4 py-3 text-left font-medium">Name</th>
                      <th className="px-4 py-3 text-left font-medium">Duration</th>
                      <th className="px-4 py-3 text-left font-medium">Early Start</th>
                      <th className="px-4 py-3 text-left font-medium">Early Finish</th>
                      <th className="px-4 py-3 text-left font-medium">Total Float</th>
                      <th className="px-4 py-3 text-left font-medium">Status</th>
                      <th className="px-4 py-3 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((act) => (
                      <tr key={act.id} className={`border-t hover:bg-slate-50 ${act.is_critical ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3 font-mono text-xs">{act.activity_id}</td>
                        <td className="px-4 py-3 font-medium">{act.name}</td>
                        <td className="px-4 py-3">{act.original_duration}d</td>
                        <td className="px-4 py-3">{act.early_start || '-'}</td>
                        <td className="px-4 py-3">{act.early_finish || '-'}</td>
                        <td className="px-4 py-3">
                          {act.total_float !== null ? (
                            <span className={act.total_float <= 0 ? 'text-red-600 font-medium' : ''}>
                              {act.total_float}d
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {act.is_critical && <Badge variant="destructive">Critical</Badge>}
                          {act.actual_finish && <Badge variant="success">Complete</Badge>}
                        </td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteActivity(act.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Relationships Tab */}
          <TabsContent value="relationships" className="flex-1 m-0 overflow-auto">
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Relationships</h3>
                <Button onClick={() => setShowAddRelationship(true)} disabled={activities.length < 2} data-testid="add-relationship-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Relationship
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Predecessor</th>
                      <th className="px-4 py-3 text-left font-medium">Type</th>
                      <th className="px-4 py-3 text-left font-medium">Lag</th>
                      <th className="px-4 py-3 text-left font-medium">Successor</th>
                      <th className="px-4 py-3 text-left font-medium">Driving</th>
                      <th className="px-4 py-3 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relationships.map((rel) => {
                      const pred = activities.find(a => a.id === rel.predecessor_id);
                      const succ = activities.find(a => a.id === rel.successor_id);
                      return (
                        <tr key={rel.id} className="border-t hover:bg-slate-50">
                          <td className="px-4 py-3">{pred?.name || rel.predecessor_id}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{rel.relationship_type}</Badge>
                          </td>
                          <td className="px-4 py-3">{rel.lag}d</td>
                          <td className="px-4 py-3">{succ?.name || rel.successor_id}</td>
                          <td className="px-4 py-3">
                            {rel.is_driving && <Badge variant="success">Driving</Badge>}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                await api.cpm.deleteRelationship(projectId, rel.id);
                                toast.success('Relationship deleted');
                                fetchData();
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Baselines Tab */}
          <TabsContent value="baselines" className="flex-1 m-0 overflow-auto">
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Baselines</h3>
                <Button onClick={() => setShowCreateBaseline(true)} disabled={activities.length === 0} data-testid="create-baseline-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Baseline
                </Button>
              </div>
              <div className="grid gap-4">
                {baselines.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6 text-center text-slate-500">
                      No baselines created yet. Run compute first, then create a baseline to snapshot the schedule.
                    </CardContent>
                  </Card>
                ) : (
                  baselines.map((bl) => (
                    <Card key={bl.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {bl.name}
                              {bl.is_primary && <Badge>Primary</Badge>}
                            </CardTitle>
                            <CardDescription>
                              Created: {new Date(bl.created_at).toLocaleDateString()} | Data Date: {bl.data_date}
                            </CardDescription>
                          </div>
                          <Badge variant="outline">{bl.activities?.length || 0} activities</Badge>
                        </div>
                      </CardHeader>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Quality Tab */}
          <TabsContent value="quality" className="flex-1 m-0 overflow-auto">
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Schedule Quality</h3>
                <Button onClick={handleQualityCheck} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" /> Run Check
                </Button>
              </div>

              {qualityReport ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold">{qualityReport.health_score}</div>
                        <div className="text-sm text-slate-500">Health Score</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-red-600">{qualityReport.summary?.errors || 0}</div>
                        <div className="text-sm text-slate-500">Errors</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-amber-600">{qualityReport.summary?.warnings || 0}</div>
                        <div className="text-sm text-slate-500">Warnings</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-blue-600">{qualityReport.summary?.info || 0}</div>
                        <div className="text-sm text-slate-500">Info</div>
                      </CardContent>
                    </Card>
                  </div>

                  {qualityReport.issues?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Issues Found</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {qualityReport.issues.map((issue, idx) => (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg ${
                                issue.severity === 'error' ? 'bg-red-50 border-red-200' :
                                issue.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                                'bg-blue-50 border-blue-200'
                              } border`}
                            >
                              <div className="flex items-center gap-2">
                                <Badge variant={issue.severity === 'error' ? 'destructive' : issue.severity === 'warning' ? 'warning' : 'secondary'}>
                                  {issue.type}
                                </Badge>
                                <span className="font-medium">{issue.activity_name}</span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{issue.message}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent className="pt-6 text-center text-slate-500">
                    Click "Run Check" to analyze schedule quality
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Activity Dialog */}
      <Dialog open={showAddActivity} onOpenChange={setShowAddActivity}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
            <DialogDescription>Create a new activity in the schedule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Activity ID</Label>
                <Input
                  value={activityForm.activity_id}
                  onChange={(e) => setActivityForm({ ...activityForm, activity_id: e.target.value })}
                  placeholder="A1010"
                  data-testid="activity-id-input"
                />
              </div>
              <div>
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  value={activityForm.original_duration}
                  onChange={(e) => setActivityForm({ ...activityForm, original_duration: parseInt(e.target.value) || 0 })}
                  data-testid="activity-duration-input"
                />
              </div>
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={activityForm.name}
                onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                placeholder="Activity name"
                data-testid="activity-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select
                  value={activityForm.activity_type}
                  onValueChange={(v) => setActivityForm({ ...activityForm, activity_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="milestone">Milestone</SelectItem>
                    <SelectItem value="summary">Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Constraint</Label>
                <Select
                  value={activityForm.constraint_type}
                  onValueChange={(v) => setActivityForm({ ...activityForm, constraint_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">As Soon As Possible</SelectItem>
                    <SelectItem value="start_no_earlier_than">Start No Earlier Than</SelectItem>
                    <SelectItem value="start_no_later_than">Start No Later Than</SelectItem>
                    <SelectItem value="must_start_on">Must Start On</SelectItem>
                    <SelectItem value="must_finish_on">Must Finish On</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                placeholder="Optional description..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivity(false)}>Cancel</Button>
            <Button onClick={handleAddActivity} data-testid="save-activity-btn">Add Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Relationship Dialog */}
      <Dialog open={showAddRelationship} onOpenChange={setShowAddRelationship}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Relationship</DialogTitle>
            <DialogDescription>Link two activities with a dependency</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Predecessor</Label>
              <Select
                value={relationshipForm.predecessor_id}
                onValueChange={(v) => setRelationshipForm({ ...relationshipForm, predecessor_id: v })}
              >
                <SelectTrigger data-testid="predecessor-select">
                  <SelectValue placeholder="Select predecessor" />
                </SelectTrigger>
                <SelectContent>
                  {activities.filter(a => a.id !== relationshipForm.successor_id).map((act) => (
                    <SelectItem key={act.id} value={act.id}>{act.activity_id} - {act.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select
                  value={relationshipForm.relationship_type}
                  onValueChange={(v) => setRelationshipForm({ ...relationshipForm, relationship_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FS">Finish-to-Start (FS)</SelectItem>
                    <SelectItem value="SS">Start-to-Start (SS)</SelectItem>
                    <SelectItem value="FF">Finish-to-Finish (FF)</SelectItem>
                    <SelectItem value="SF">Start-to-Finish (SF)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lag (days)</Label>
                <Input
                  type="number"
                  value={relationshipForm.lag}
                  onChange={(e) => setRelationshipForm({ ...relationshipForm, lag: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <Label>Successor</Label>
              <Select
                value={relationshipForm.successor_id}
                onValueChange={(v) => setRelationshipForm({ ...relationshipForm, successor_id: v })}
              >
                <SelectTrigger data-testid="successor-select">
                  <SelectValue placeholder="Select successor" />
                </SelectTrigger>
                <SelectContent>
                  {activities.filter(a => a.id !== relationshipForm.predecessor_id).map((act) => (
                    <SelectItem key={act.id} value={act.id}>{act.activity_id} - {act.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRelationship(false)}>Cancel</Button>
            <Button onClick={handleAddRelationship} data-testid="save-relationship-btn">Add Relationship</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Baseline Dialog */}
      <Dialog open={showCreateBaseline} onOpenChange={setShowCreateBaseline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Baseline</DialogTitle>
            <DialogDescription>Snapshot the current schedule as a baseline</DialogDescription>
          </DialogHeader>
          <BaselineForm onSubmit={handleCreateBaseline} onCancel={() => setShowCreateBaseline(false)} />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Simple baseline form component
const BaselineForm = ({ onSubmit, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  return (
    <div className="space-y-4">
      <div>
        <Label>Baseline Name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Baseline 1 - Contract Award"
          data-testid="baseline-name-input"
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description..."
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(name, description)} disabled={!name} data-testid="save-baseline-btn">
          Create Baseline
        </Button>
      </DialogFooter>
    </div>
  );
};

export default CPMScheduler;
